import { useState, useCallback, useMemo } from 'react';
import { Subscription, SubscriptionCounts } from '..';

import { checkIfSubDifferentThanInactiveExists } from '../helpers/checkIfSubDifferentThanInactiveExists';
import { getSubscriptionCounts } from '../helpers/getSubscriptionCounts';
import { useLoadYourPurchasesCard } from './useLoadYourPurchasesCard';
import { useLoadInstantInkCard } from './useLoadInstantInkCard';
import { useLoadHpAllInCard } from './useLoadHpAllInCard';
import { IFrameCardProps } from '../FrameCardTemplate';
import { getCardKey } from '../utils/getCardKey';

interface FeatureFlags {
  isLoading: boolean;
  homeCountryBasedExperience: boolean;
  securityHPIDPassword: boolean;
}

interface UseFrameCardsLoaderProps {
  subscriptions: Subscription[] | null | undefined;
  isSubscriptionsLoading: boolean;
  translate: (key: string, options?: Record<string, any>) => string;
  language: string;
  country: string;
  featureFlags: FeatureFlags;
}

export function useFrameCardsLoader({
  subscriptions,
  isSubscriptionsLoading,
  translate,
  language,
  country,
  featureFlags
}: UseFrameCardsLoaderProps): {
  loadedCards: IFrameCardProps[];
  hasError: boolean;
  allCardsResolved: boolean;
  subscriptionCounts: SubscriptionCounts;
  instantInkSubDifferentThanInactiveExists: boolean;
  hpAllInSubDifferentThanInactiveExists: boolean;
} {
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  const {
    instantInkSubDifferentThanInactiveExists,
    hpAllInSubDifferentThanInactiveExists
  } = useMemo(
    () => checkIfSubDifferentThanInactiveExists(subscriptions),
    [subscriptions]
  );

  const subscriptionCounts = useMemo(
    () => getSubscriptionCounts(subscriptions),
    [subscriptions]
  );

  const { resolved: yourPurchasesResolved, card: yourPurchasesCard } =
    useLoadYourPurchasesCard({
      translate,
      language,
      canUseFeatureFlag: !hasError && featureFlags.homeCountryBasedExperience,
      isLoading: featureFlags.isLoading,
      onError: handleError
    });

  const { resolved: instantInkResolved, card: instantInkCard } =
    useLoadInstantInkCard({
      translate,
      language,
      country,
      instantInkSubDifferentThanInactiveExists,
      isSubscriptionsLoading,
      onError: handleError
    });

  const { resolved: hpAllInResolved, card: hpAllInCard } = useLoadHpAllInCard({
    translate,
    language,
    country,
    hpAllInSubDifferentThanInactiveExists,
    isSubscriptionsLoading
  });

  const loadedCards = useMemo(() => {
    const cards: IFrameCardProps[] = [];
    if (yourPurchasesResolved && yourPurchasesCard)
      cards.push(yourPurchasesCard);
    if (hpAllInResolved && hpAllInCard && yourPurchasesResolved)
      cards.push(hpAllInCard);
    if (instantInkResolved && instantInkCard && hpAllInResolved)
      cards.push(instantInkCard);

    return cards.filter(
      (card, idx, arr) =>
        idx === arr.findIndex((c) => getCardKey(c) === getCardKey(card))
    );
  }, [
    yourPurchasesResolved,
    yourPurchasesCard,
    hpAllInResolved,
    hpAllInCard,
    instantInkResolved,
    instantInkCard
  ]);

  const allCardsResolved =
    !isSubscriptionsLoading &&
    yourPurchasesResolved &&
    (instantInkSubDifferentThanInactiveExists ? instantInkResolved : true) &&
    (hpAllInSubDifferentThanInactiveExists ? hpAllInResolved : true);

  return {
    loadedCards,
    hasError,
    allCardsResolved,
    subscriptionCounts,
    instantInkSubDifferentThanInactiveExists,
    hpAllInSubDifferentThanInactiveExists
  };
}
